import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import SeoSiloLinks from "@/components/seo/SeoSiloLinks";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import EditorialEnrichment from "@/components/seo/EditorialEnrichment";
import Breadcrumbs from "@/components/Breadcrumbs";
import LastUpdated from "@/components/LastUpdated";
import { Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { Tv, Smartphone, Zap, Shield, Timer, Swords, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";
import FloatingTestCTA from "@/components/FloatingTestCTA";
import { useSeoPageView } from "@/hooks/useSeoPageView";
import { buildArticleSchema, buildBreadcrumbJsonLd, organizationSchema } from "@/lib/seo-schemas";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const UfcFightPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: fight, isLoading } = useQuery({
    queryKey: ["ufc-fight", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ufc_fights")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // Fetch 3 related fights for "Voir aussi"
  const { data: relatedFights } = useQuery({
    queryKey: ["ufc-related", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("ufc_fights")
        .select("slug, fighter_a, fighter_b, event, fight_date")
        .eq("published", true)
        .neq("slug", slug!)
        .order("fight_date", { ascending: true })
        .limit(3);
      return data || [];
    },
    enabled: !!slug,
  });

  // ✅ Hook must be called before any conditional return (Rules of Hooks)
  useSeoPageView("ufc", slug, fight ? `${fight.fighter_a} vs ${fight.fighter_b}` : undefined);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!fight) return <NotFound />;

  const fightTitle = `${fight.fighter_a} vs ${fight.fighter_b}`;
  const fightDateFormatted = new Date(fight.fight_date).toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  });

  const title = `${fight.event} ${fightTitle} streaming HD – Combat complet`;
  const description = `Regardez ${fightTitle} en streaming live HD. ${fight.event} le ${new Date(fight.fight_date).toLocaleDateString("fr-FR")}. UFC, MMA en direct sur RoiDuStreaming.`;
  const canonicalUrl = `https://roidustreaming.fr/ufc/${fight.slug}`;
  const fightImage = fight.image_url || "https://roidustreaming.fr/images/default.jpg";
  const imageAlt = `Combat UFC ${fightTitle}`;

  const startDateTime = `${fight.fight_date}T22:00:00`;
  const endDate = (() => {
    const d = new Date(startDateTime);
    d.setHours(d.getHours() + 4);
    return d.toISOString();
  })();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: fightTitle,
    description,
    url: canonicalUrl,
    startDate: startDateTime,
    endDate,
    sport: "MMA",
    homeTeam: { "@type": "Person", name: fight.fighter_a },
    awayTeam: { "@type": "Person", name: fight.fighter_b },
    competitor: [
      { "@type": "Person", name: fight.fighter_a },
      { "@type": "Person", name: fight.fighter_b },
    ],
    performer: [
      { "@type": "Person", name: fight.fighter_a },
      { "@type": "Person", name: fight.fighter_b },
    ],
    ...(fight.image_url && { image: fight.image_url }),
    location: {
      "@type": "Place",
      name: fight.event,
      address: {
        "@type": "PostalAddress",
        addressCountry: "US",
      },
    },
    organizer: { "@type": "Organization", name: "RoiDuStreaming", url: "https://roidustreaming.fr" },
    offers: {
      "@type": "Offer",
      name: "Abonnement IPTV RoiDuStreaming",
      price: "5.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://roidustreaming.fr/nos-offres",
      validFrom: "2025-01-01",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Quand a lieu ${fightTitle} ?`,
        acceptedAnswer: { "@type": "Answer", text: `${fightTitle} se déroule lors de ${fight.event} le ${fightDateFormatted}.` }
      },
      {
        "@type": "Question",
        name: `Comment regarder ${fightTitle} en direct ?`,
        acceptedAnswer: { "@type": "Answer", text: `Avec RoiDuStreaming, accédez à ${fightTitle} et à tous les événements UFC et MMA en direct. Abonnement à partir de 5€/mois avec essai gratuit de 24h.` }
      },
      {
        "@type": "Question",
        name: `${fightTitle} est-il diffusé en HD ?`,
        acceptedAnswer: { "@type": "Answer", text: `Oui, ${fightTitle} est diffusé en qualité Full HD et 4K sur RoiDuStreaming, sans décalage ni buffering.` }
      },
      {
        "@type": "Question",
        name: `Quels événements MMA sont disponibles ?`,
        acceptedAnswer: { "@type": "Answer", text: `UFC Fight Night, UFC PPV, Bellator, PFL, ONE Championship... Tous les grands événements dont ${fightTitle} sont disponibles sur RoiDuStreaming.` }
      }
    ]
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "UFC", href: "/ufc" },
    { label: fightTitle, href: `/ufc/${fight.slug}` },
  ];

  const advantages = [
    { icon: Tv, title: "Full HD & 4K", desc: "Image cristalline en direct" },
    { icon: Zap, title: "Zéro décalage", desc: "Streaming live sans lag" },
    { icon: Smartphone, title: "Multi-appareils", desc: "Smart TV, mobile, tablette, PC" },
    { icon: Shield, title: "Accès sécurisé", desc: "Paiement Stripe 100% sécurisé" },
    { icon: Timer, title: "Toutes les cartes", desc: "Préliminaires + carte principale" },
    { icon: Swords, title: "UFC + Bellator", desc: "Tous les événements MMA" },
  ];

  const breadcrumbSchema = buildBreadcrumbJsonLd([
    { name: "Accueil", href: "/" },
    { name: "UFC", href: "/ufc" },
    { name: fightTitle, href: `/ufc/${fight.slug}` },
  ]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="RoiDuStreaming" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content={fightImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={fightImage} />
        <meta property="article:published_time" content={`${fight.fight_date}T00:00:00Z`} />
        <meta property="article:section" content="UFC MMA" />
        <meta property="article:tag" content={fight.event} />
        <meta property="article:tag" content={fight.fighter_a} />
        <meta property="article:tag" content={fight.fighter_b} />
        <link rel="preconnect" href="https://tpujpzojoqwytuhgbwtw.supabase.co" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(buildArticleSchema({
          headline: `${fightTitle} ${fight.event} Streaming en Direct`,
          description,
          url: canonicalUrl,
          imageUrl: fight.image_url,
          datePublished: fight.created_at || fight.fight_date,
          dateModified: fight.created_at || new Date().toISOString(),
          section: "UFC MMA",
        }))}</script>
      </Helmet>

      {/* ── NOSCRIPT FALLBACK : données critiques visibles sans JS ── */}
      <noscript>
        <div>
          <h1>{fightTitle} – {fight.event} – Streaming en Direct</h1>
          <p>Combat UFC/MMA : {fight.fighter_a} affronte {fight.fighter_b} lors de {fight.event} le {fight.fight_date}.</p>
          <p>Regardez {fightTitle} en direct HD sur RoiDuStreaming, à partir de 5€/mois. Essai gratuit 24h disponible.</p>
          <ul>
            <li><a href="https://roidustreaming.fr">Accueil</a></li>
            <li><a href="https://roidustreaming.fr/ufc">UFC & MMA en streaming</a></li>
            <li><a href="https://roidustreaming.fr/nos-offres">Offres IPTV</a></li>
          </ul>
        </div>
      </noscript>

      <article className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs items={breadcrumbItems} />
        <div className="max-w-6xl mx-auto px-4 pt-3">
          <LastUpdated date={fight.created_at || new Date().toISOString()} />
        </div>

        {/* Hero */}
        <section className="relative min-h-[50vh] flex items-center px-4 overflow-hidden">
          {fight.image_url && (
            <img src={fight.image_url} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover blur-sm scale-105" loading="eager" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />

          <div className="relative z-10 max-w-6xl mx-auto w-full py-16 text-center">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full text-sm font-bold bg-destructive/20 text-destructive border border-destructive/30">
              🥊 {fight.event}
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-4 text-foreground leading-tight">
              {fight.fighter_a} vs {fight.fighter_b}
              <span className="block text-primary text-xl md:text-2xl mt-2 font-bold">en Streaming Direct HD & 4K</span>
            </h1>
            <p className="text-muted-foreground text-lg mb-6">
              📅 {fightDateFormatted}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <TestRequestFormWithCountries
                trigger={
                  <Button className="btn-primary text-primary-foreground font-bold py-5 sm:py-6 px-6 sm:px-8 text-sm sm:text-base rounded-full group w-full sm:w-auto">
                    🥊 Essayer gratuitement 24h
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                }
              />
              <Link to="/nos-offres" className="w-full sm:w-auto">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 font-bold py-5 sm:py-6 px-6 sm:px-8 text-sm sm:text-base rounded-full w-full sm:w-auto">
                  Voir les offres dès 5€/mois
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-y border-border/50 bg-card/50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span>⚡ Installation en 5 min</span>
            <span>🛟 Support 7j/7</span>
            <span>📺 +15 000 chaînes TV</span>
            <span>🔒 Paiement sécurisé</span>
          </div>
        </section>

        {/* ── TABLE 1 : INFORMATIONS DU COMBAT ── */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Informations du combat</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium w-1/2 bg-card/50">Fighter A (Coin rouge)</th>
                    <td className="px-4 py-3 font-semibold text-foreground">{fight.fighter_a}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Fighter B (Coin bleu)</th>
                    <td className="px-4 py-3 font-semibold text-foreground">{fight.fighter_b}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Événement</th>
                    <td className="px-4 py-3 text-foreground font-semibold">{fight.event}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Date du combat</th>
                    <td className="px-4 py-3 text-foreground">{fightDateFormatted}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Discipline</th>
                    <td className="px-4 py-3 text-foreground">Arts martiaux mixtes (MMA / UFC)</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Format du combat</th>
                    <td className="px-4 py-3 text-foreground">Carte préliminaire + Carte principale</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Diffusion</th>
                    <td className="px-4 py-3 text-foreground">RoiDuStreaming – Full HD & 4K</td>
                  </tr>
                  <tr>
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Accès</th>
                    <td className="px-4 py-3 text-foreground font-semibold text-secondary">À partir de 5€/mois — Essai 24h gratuit</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── TABLE 2 : FACE-À-FACE ── */}
        <section className="py-6 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Face-à-face — {fight.fighter_a} vs {fight.fighter_b}</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-primary/5">
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Critère</th>
                    <th className="text-center px-4 py-3 font-semibold text-foreground">{fight.fighter_a}</th>
                    <th className="text-center px-4 py-3 font-semibold text-foreground">{fight.fighter_b}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Classement</td>
                    <td className="px-4 py-3 text-center text-foreground font-semibold">Coin rouge</td>
                    <td className="px-4 py-3 text-center text-foreground font-semibold">Coin bleu</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Style de combat</td>
                    <td className="px-4 py-3 text-center text-foreground">MMA / Polyvalent</td>
                    <td className="px-4 py-3 text-center text-foreground">MMA / Polyvalent</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Événement</td>
                    <td className="px-4 py-3 text-center text-foreground" colSpan={2}>{fight.event}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Date</td>
                    <td className="px-4 py-3 text-center text-foreground" colSpan={2}>{fightDateFormatted}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Où regarder</td>
                    <td className="px-4 py-3 text-center text-foreground font-semibold text-secondary" colSpan={2}>RoiDuStreaming HD & 4K</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 text-foreground">
              Regardez <span className="text-primary">{fightTitle}</span> en direct
            </h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
              Le combat sera diffusé en direct sur notre plateforme IPTV. Accédez à tous les événements UFC et MMA en qualité HD/4K sans lag.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {advantages.map((adv, i) => (
                <div key={i} className="p-5 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-center group">
                  <adv.icon className="w-8 h-8 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-foreground mb-1">{adv.title}</h3>
                  <p className="text-sm text-muted-foreground">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto prose prose-invert prose-sm">
            <h2 className="text-xl font-bold text-foreground">
              {fightTitle} en streaming direct sur RoiDuStreaming
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Ne manquez pas <strong>{fightTitle}</strong> lors de <strong>{fight.event}</strong>. RoiDuStreaming diffuse tous les événements UFC et MMA en direct, en qualité Full HD et 4K. Accédez aux préliminaires et à la carte principale sans aucune coupure depuis votre <Link to="/tutoriels/iptv-smart-tv-android" className="text-primary hover:underline">Smart TV</Link>, <Link to="/tutoriels/iptv-android" className="text-primary hover:underline">Android</Link>, <Link to="/tutoriels/iptv-iphone-ipad" className="text-primary hover:underline">iPhone</Link> ou <Link to="/tutoriels/iptv-firestick" className="text-primary hover:underline">Fire Stick</Link>.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Avec un <Link to="/nos-offres" className="text-primary hover:underline">abonnement IPTV dès 5€/mois</Link>, profitez aussi de +15 000 chaînes TV, +100 000 films et +25 000 séries. Essai gratuit 24h disponible. Voir nos <Link to="/avis-clients" className="text-primary hover:underline">avis clients</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border border-primary/30 p-6 sm:p-8 md:p-12 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-foreground">
              Ne ratez pas {fightTitle} !
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto text-sm sm:text-base">
              Tous les événements UFC, Bellator et MMA en direct. +15 000 chaînes TV, +100 000 films. À partir de 5€/mois.
            </p>
            <TestRequestFormWithCountries
              trigger={
                <Button className="btn-primary text-primary-foreground font-bold py-5 sm:py-6 px-6 sm:px-10 text-sm sm:text-base rounded-full group w-full sm:w-auto">
                  Démarrer mon essai gratuit 24h
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              }
            />
          </div>
        </section>

        {/* ── FAQ DYNAMIQUE ── */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-foreground">
              Questions fréquentes sur {fightTitle}
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: `Quand a lieu ${fightTitle} ?`, a: `${fightTitle} se déroule lors de ${fight.event} le ${fightDateFormatted}.` },
                { q: `Comment regarder ${fightTitle} en direct ?`, a: `Avec RoiDuStreaming, accédez à ${fightTitle} et à tous les événements UFC et MMA en direct. Abonnement à partir de 5€/mois avec essai gratuit de 24h.` },
                { q: `${fightTitle} est-il diffusé en HD ?`, a: `Oui, ${fightTitle} est diffusé en qualité Full HD et 4K sur RoiDuStreaming, sans décalage ni buffering.` },
                { q: `Quels événements MMA sont disponibles ?`, a: `UFC Fight Night, UFC PPV, Bellator, PFL, ONE Championship... Tous les grands événements dont ${fightTitle} sont disponibles sur RoiDuStreaming.` },
                { q: `Puis-je tester avant de m'abonner ?`, a: `Absolument ! Profitez d'un essai gratuit de 24h avec accès complet à toutes les chaînes sportives et au catalogue complet.` },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-xl px-5 bg-card/50">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── VOIR AUSSI ── */}
        {relatedFights && relatedFights.length > 0 && (
          <section className="py-10 px-4 bg-card/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground mb-5">Voir aussi – Prochains combats UFC</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {relatedFights.map((f) => (
                  <Link
                    key={f.slug}
                    to={`/ufc/${f.slug}`}
                    className="flex flex-col gap-1 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all group"
                  >
                    <span className="text-xs text-muted-foreground">{f.event}</span>
                    <span className="font-bold text-foreground group-hover:text-primary transition-colors text-sm">
                      {f.fighter_a} vs {f.fighter_b}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      📅 {new Date(f.fight_date).toLocaleDateString("fr-FR")}
                      <ChevronRight className="w-3 h-3 ml-auto" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Editorial Enrichment ── */}
        <EditorialEnrichment content={(fight as any).editorial_content} />

        {/* Offers */}
        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>

        {/* Internal Links */}
        <PopularPagesBlock />
        <RecentPagesBlock />
        <SeoSiloLinks category="ufc" currentSlug={slug} />

        <Footer />
        <FloatingTestCTA />
      </article>
    </>
  );
};

export default UfcFightPage;
